import defaultLogoIcon from '../assets/images/coloredIcon.png';
import defaultLogoFull from '../assets/images/logo.png';

/**
 * Raw whitelabel config as defined in the JSON config file.
 * Injected at build time via Vite's `define` option.
 */
export interface WhitelabelConfig {
  /** Display name shown in UI (welcome text, alt tags) */
  appName: string;

  /** Browser tab / meta title. Falls back to appName if not set. */
  pageTitle?: string | null;

  /** Primary brand color hex (e.g. "#2563eb"). Palette auto-generated from this. null = use SuperFunnel defaults. */
  brandColor: string | null;

  logos: {
    /** Full wordmark logo (sidebar expanded, login, onboarding). null = SuperFunnel default. */
    full: string | null;
    /** Icon-only logo (sidebar collapsed, AI assistant). null = SuperFunnel default. */
    icon: string | null;
  };

  /** Favicon URL/path. null = use existing favicon.ico. */
  favicon: string | null;

  /** PWA icons. null = use existing defaults. */
  pwaIcons: {
    icon192: string;
    icon512: string;
  } | null;
}

/**
 * Resolved whitelabel config with all defaults filled in.
 * This is what components should consume.
 */
export interface ResolvedWhitelabelConfig {
  appName: string;
  pageTitle: string;
  brandColor: string | null;
  logoFull: string;
  logoIcon: string;
  favicon: string | null;
  isWhitelabel: boolean;
}

// Injected by Vite at build time via `define`
declare const __WHITELABEL_CONFIG__: WhitelabelConfig;

const rawConfig: WhitelabelConfig =
  typeof __WHITELABEL_CONFIG__ !== 'undefined'
    ? __WHITELABEL_CONFIG__
    : {
        appName: 'SuperFunnel',
        brandColor: null,
        logos: { full: null, icon: null },
        favicon: null,
        pwaIcons: null,
      };

/**
 * The fully resolved whitelabel config with defaults applied.
 * Import this directly for non-React code, or use `useWhitelabel()` hook in components.
 */
export const whitelabelConfig: ResolvedWhitelabelConfig = {
  appName: rawConfig.appName,
  pageTitle: rawConfig.pageTitle || rawConfig.appName,
  brandColor: rawConfig.brandColor,
  logoFull: rawConfig.logos.full ?? defaultLogoFull,
  logoIcon: rawConfig.logos.icon ?? defaultLogoIcon,
  favicon: rawConfig.favicon,
  isWhitelabel: rawConfig.appName !== 'SuperFunnel',
};
